import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Admin.css';
import '../styles/ICOListing.css';

import Navbar from '../components/Navbar';

import {
    getAdminStats, getAdminProjects, adminProjectAction,
    getAdminUsers, adminUserAction, getAdminRevenue,
    getAdminBoosts, adminBoostAction, getAdminSettings, updateSetting,
    getAdminCardBanners, adminCardBannerAction,
    getRelanceProjects, getRelanceTemplates, saveRelanceTemplate,
    deleteRelanceTemplate, sendRelance
} from '../api/projects';

const TAB_TO_SLUG = {
    'dashboard':    'dashboard',
    'projets':      'projets',
    'utilisateurs': 'utilisateurs',
    'boosts':       'boosts',
    'card images':  'card-images',
    'relance':      'relance',
    'paramètres':   'parametres',
};
const SLUG_TO_TAB = Object.fromEntries(Object.entries(TAB_TO_SLUG).map(([k, v]) => [v, k]));

export default function Admin() {
    const navigate = useNavigate();
    const { tabSlug } = useParams();
    const { user, logoutUser } = useAuth();

    const [tab, setTab] = useState(SLUG_TO_TAB[tabSlug] || 'dashboard');

    // Sync tab avec l'URL (navigation directe ou back/forward)
    useEffect(() => {
        const newTab = SLUG_TO_TAB[tabSlug] || 'dashboard';
        setTab(newTab);
    }, [tabSlug]);
    const [stats, setStats]               = useState(null);
    const [projects, setProjects]         = useState([]);
    const [users, setUsers]               = useState([]);
    const [projectFilter, setProjectFilter] = useState('all');
    const [loading, setLoading]           = useState(true);
    const [rejectModal, setRejectModal]   = useState(null);
    const [rejectNote, setRejectNote]     = useState('');
    const [sortField, setSortField]       = useState('created_at');
    const [sortDir, setSortDir]           = useState('desc');
    const [revenue, setRevenue]           = useState(null);
    const [boosts, setBoosts]             = useState([]);
    const [boostFilter, setBoostFilter]   = useState('pending');
    const [copiedId, setCopiedId]         = useState(null);
    const [settings, setSettings]         = useState([]);
    const [settingsSaved, setSettingsSaved] = useState(false);
    const [projectStats, setProjectStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
    const [cardBanners, setCardBanners]   = useState([]);
    const [cardBannerFilter, setCardBannerFilter] = useState('pending');
    const [projectSearch, setProjectSearch] = useState('');
    const [userSearch, setUserSearch]       = useState('');
    const [userSortField, setUserSortField] = useState('created_at');
    const [userSortDir, setUserSortDir]     = useState('desc');

    // RELANCE
    const [relanceProjects, setRelanceProjects]     = useState([]);
    const [relanceTemplates, setRelanceTemplates]   = useState([]);
    const [relanceSelected, setRelanceSelected]     = useState([]);
    const [relanceTemplateId, setRelanceTemplateId] = useState(null);
    const [relanceConfirm, setRelanceConfirm]       = useState(false);
    const [relanceResult, setRelanceResult]         = useState(null);
    const [relanceSending, setRelanceSending]       = useState(false);
    const [relanceSearch, setRelanceSearch]         = useState('');
    const [relanceFilter, setRelanceFilter]         = useState('all'); // all | eligible | ineligible
    const [relanceSortField, setRelanceSortField]   = useState('last_relance_sent');
    const [relanceSortDir, setRelanceSortDir]       = useState('asc');
    const [tmplModal, setTmplModal]                 = useState(null);  // null | 'new' | {id,name,subject,body}
    const [tmplForm, setTmplForm]                   = useState({ name: '', type: 'manual', subject: '', body: '' });


    useEffect(() => {
        if (user === null) return;
        if (user.role !== 'admin') { navigate('/'); return; }
        loadData();
    }, [user]);

    ////////////////////////////
    // CHARGEMENT DONNÉES
    ////////////////////////////
    const loadData = async () => {
        setLoading(true);
        const [s, p, u, r, b, st, cb] = await Promise.all([
            getAdminStats().catch(() => null),
            getAdminProjects().catch(() => []),
            getAdminUsers().catch(() => []),
            getAdminRevenue().catch(() => null),
            getAdminBoosts().catch(() => []),
            getAdminSettings().catch(() => []),
            getAdminCardBanners('pending').catch(() => []),
        ]);
        setStats(s);
        setProjects(Array.isArray(p) ? p : []);
        setUsers(Array.isArray(u) ? u : []);
        setRevenue(r);
        setBoosts(Array.isArray(b) ? b : []);
        setSettings(Array.isArray(st) ? st : []);
        setCardBanners(Array.isArray(cb) ? cb : []);

        const allP = await getAdminProjects('all');
        setProjectStats({
            total:    allP.length,
            pending:  allP.filter(p => !parseInt(p.is_approved) && parseInt(p.email_verified) && p.review_status === 'pending').length,
            approved: allP.filter(p => parseInt(p.is_approved)).length,
            rejected: allP.filter(p => p.review_status === 'rejected').length,
        });
        setLoading(false);
    };

    ////////////////////////////
    // TRI PROJETS
    ////////////////////////////
    const filteredProjects = projects.filter(p =>
        !projectSearch ||
        p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
        (p.ticker || '').toLowerCase().includes(projectSearch.toLowerCase()) ||
        (p.owner || '').toLowerCase().includes(projectSearch.toLowerCase())
    );

    const sortedProjects = [...filteredProjects].sort((a, b) => {
        let valA, valB;
        switch (sortField) {
            case 'name':       valA = a.name.toLowerCase(); valB = b.name.toLowerCase(); break;
            case 'status':     valA = a.is_approved; valB = b.is_approved; break;
            case 'state':      valA = a.status || ''; valB = b.status || ''; break;
            case 'client':     valA = (a.owner || '').toLowerCase(); valB = (b.owner || '').toLowerCase(); break;
            case 'total_paid': valA = parseFloat(a.total_paid || 0); valB = parseFloat(b.total_paid || 0); break;
            case 'votes':      valA = parseInt(a.total_votes); valB = parseInt(b.total_votes); break;
            case 'boosts':     valA = parseInt(a.boosts || 0); valB = parseInt(b.boosts || 0); break;
            case 'created_at': valA = new Date(a.created_at); valB = new Date(b.created_at); break;
            case 'pubs':       valA = (a.has_banner ? 1 : 0) + (a.has_premium ? 1 : 0) + (a.has_card_image ? 1 : 0);
                               valB = (b.has_banner ? 1 : 0) + (b.has_premium ? 1 : 0) + (b.has_card_image ? 1 : 0); break;
            default:           valA = a.name; valB = b.name;
        }
        if (valA < valB) return sortDir === 'asc' ? -1 : 1;
        if (valA > valB) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const handleUserSort = (field) => {
        if (userSortField === field) setUserSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setUserSortField(field); setUserSortDir('asc'); }
    };
    const userSortIcon = (field) => userSortField === field ? (userSortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕';

    const filteredUsers = [...users]
        .filter(u =>
            !userSearch ||
            u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
            (u.project_name || '').toLowerCase().includes(userSearch.toLowerCase())
        )
        .sort((a, b) => {
            let valA, valB;
            switch (userSortField) {
                case 'username':       valA = a.username.toLowerCase(); valB = b.username.toLowerCase(); break;
                case 'email':          valA = a.email.toLowerCase(); valB = b.email.toLowerCase(); break;
                case 'role':           valA = a.role; valB = b.role; break;
                case 'project_name':   valA = (a.project_name || '').toLowerCase(); valB = (b.project_name || '').toLowerCase(); break;
                case 'email_verified': valA = parseInt(a.email_verified); valB = parseInt(b.email_verified); break;
                case 'is_active':      valA = parseInt(a.is_active ?? 1); valB = parseInt(b.is_active ?? 1); break;
                case 'created_at':     valA = new Date(a.created_at); valB = new Date(b.created_at); break;
                default:               valA = a.username; valB = b.username;
            }
            if (valA < valB) return userSortDir === 'asc' ? -1 : 1;
            if (valA > valB) return userSortDir === 'asc' ? 1 : -1;
            return 0;
        });

    const handleSort = (field) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };
    const sortIcon = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕';

    ////////////////////////////
    // ACTIONS
    ////////////////////////////
    const loadProjects = async (status) => {
        setProjectFilter(status);
        const p = await getAdminProjects(status);
        setProjects(Array.isArray(p) ? p : []);
    };

    const handleProjectAction = async (action, id, note = '', extra = '') => {
        await adminProjectAction(action, id, note, extra);
        const p = await getAdminProjects(projectFilter);
        setProjects(Array.isArray(p) ? p : []);
        const s = await getAdminStats();
        setStats(s);
    };

    const handleUserAction = async (action, id) => {
        await adminUserAction(action, id);
        const u = await getAdminUsers();
        setUsers(Array.isArray(u) ? u : []);
    };

    const handleBoostAction = async (action, id) => {
        await adminBoostAction(action, id);
        const b = await getAdminBoosts(boostFilter);
        setBoosts(Array.isArray(b) ? b : []);
        const s = await getAdminStats();
        setStats(s);
    };

    const loadBoosts = async (status) => {
        setBoostFilter(status);
        const b = await getAdminBoosts(status);
        setBoosts(Array.isArray(b) ? b : []);
    };

    const handleCardBannerAction = async (action, projectId) => {
        await adminCardBannerAction(projectId, action);
        const cb = await getAdminCardBanners(cardBannerFilter);
        setCardBanners(Array.isArray(cb) ? cb : []);
    };

    const loadCardBanners = async (status) => {
        setCardBannerFilter(status);
        const cb = await getAdminCardBanners(status);
        setCardBanners(Array.isArray(cb) ? cb : []);
    };

    ////////////////////////////
    // RELANCE — chargement
    ////////////////////////////
    const loadRelance = async () => {
        const [rp, rt] = await Promise.all([
            getRelanceProjects().catch(() => ({ projects: [] })),
            getRelanceTemplates().catch(() => ({ templates: [] })),
        ]);
        setRelanceProjects(Array.isArray(rp.projects) ? rp.projects : []);
        setRelanceTemplates(Array.isArray(rt.templates) ? rt.templates : []);
        if (!relanceTemplateId && rt.templates?.length) setRelanceTemplateId(rt.templates[0].id);
    };

    useEffect(() => { if (tab === 'relance') loadRelance(); }, [tab]);
    useEffect(() => { if (tabSlug === 'projets') loadProjects(projectFilter); }, [tabSlug]);

    const handleRelanceSort = (field) => {
        if (relanceSortField === field) setRelanceSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setRelanceSortField(field); setRelanceSortDir('asc'); }
    };
    const relanceSortIcon = (field) => relanceSortField === field ? (relanceSortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕';

    const relanceFiltered = relanceProjects
        .filter(p => {
            if (relanceFilter === 'eligible')   return parseInt(p.relance_eligible) === 1;
            if (relanceFilter === 'ineligible') return parseInt(p.relance_eligible) === 0;
            return true;
        })
        .filter(p => !relanceSearch || p.name.toLowerCase().includes(relanceSearch.toLowerCase()))
        .sort((a, b) => {
            let valA, valB;
            switch (relanceSortField) {
                case 'name':               valA = a.name || '';                   valB = b.name || '';                   break;
                case 'type':               valA = a.type || '';                   valB = b.type || '';                   break;
                case 'owner_email':        valA = a.owner_email || '';            valB = b.owner_email || '';            break;
                case 'active_boosts':      valA = parseInt(a.active_boosts) || 0; valB = parseInt(b.active_boosts) || 0; break;
                case 'quest_count':        valA = parseInt(a.quest_count) || 0;   valB = parseInt(b.quest_count) || 0;   break;
                case 'last_relance_sent':  valA = a.last_relance_sent || '';      valB = b.last_relance_sent || '';      break;
                case 'relance_eligible':   valA = parseInt(a.relance_eligible);   valB = parseInt(b.relance_eligible);   break;
                default:                   valA = a.last_relance_sent || '';      valB = b.last_relance_sent || '';
            }
            if (valA < valB) return relanceSortDir === 'asc' ? -1 : 1;
            if (valA > valB) return relanceSortDir === 'asc' ? 1 : -1;
            return 0;
        });

    const allEligibleIds = relanceFiltered
        .filter(p => parseInt(p.relance_eligible) === 1)
        .map(p => p.id);

    const toggleRelanceSelect = (id) => {
        setRelanceSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    };

    const toggleSelectAll = () => {
        if (relanceSelected.length === allEligibleIds.length) setRelanceSelected([]);
        else setRelanceSelected(allEligibleIds);
    };

    const handleSendRelance = async () => {
        if (!relanceSelected.length || !relanceTemplateId) return;
        setRelanceSending(true);
        const res = await sendRelance(relanceSelected, relanceTemplateId);
        setRelanceResult(res);
        setRelanceSending(false);
        setRelanceConfirm(false);
        setRelanceSelected([]);
        loadRelance();
    };


    const handleSaveTemplate = async () => {
        await saveRelanceTemplate(tmplForm);
        setTmplModal(null);
        setTmplForm({ name: '', type: 'manual', subject: '', body: '' });
        loadRelance();
    };

    const handleDeleteTemplate = async (id) => {
        if (!window.confirm('Supprimer ce template ?')) return;
        await deleteRelanceTemplate(id);
        loadRelance();
    };

    const openEditTemplate = (t) => {
        setTmplForm({ id: t.id, name: t.name, type: t.type || 'manual', subject: t.subject, body: t.body });
        setTmplModal('edit');
    };

    const colors = ['#34D399', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#10B981'];

    if (loading) return <div className="app-wrap"><div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>Loading...</div></div>;

    return (
        <div className="app-wrap">
            
            <Navbar />

            <div className="admin-wrap">
                <div className="admin-header">
                    <div className="admin-title">⚙ Administration</div>
                    {stats?.pending_projects > 0 && (
                        <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '20px', padding: '6px 16px', color: '#F59E0B', fontSize: '0.85rem' }}>
                            ⚠ {stats.pending_projects} projet{stats.pending_projects > 1 ? 's' : ''} en attente
                        </div>
                    )}
                </div>

                {/* TABS */}
                <div className="admin-tabs">
                    {['dashboard', 'projets', 'utilisateurs', 'boosts', 'card images', 'relance', 'paramètres'].map(t => (
                        <button key={t} className={`admin-tab${tab === t ? ' active' : ''}`} onClick={() => navigate(`/admin/${TAB_TO_SLUG[t]}`)}>
                            {t === 'card images' ? '🖼️ Card Images' : t === 'relance' ? '📧 Relance' : t.charAt(0).toUpperCase() + t.slice(1)}
                            {t === 'card images' && cardBanners.length > 0 && cardBannerFilter === 'pending' && (
                                <span style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B', borderRadius: '20px', padding: '1px 7px', fontSize: '0.7rem', marginLeft: '6px' }}>{cardBanners.length}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* ======================== DASHBOARD ======================== */}
                {tab === 'dashboard' && stats && revenue && (
                    <>
                        {/* STATS PROJETS */}
                        <div className="stats-cards" style={{ marginBottom: '24px' }}>
                            {[
                                { label: 'Total projets', val: `${stats.approved_projects}/${stats.total_projects}`, color: '#34D399' },
                                { label: 'En attente',    val: stats.pending_projects, color: '#F59E0B' },
                                { label: 'Utilisateurs',  val: stats.total_users,      color: '#8B5CF6' },
                            ].map((item, i) => (
                                <div className="stat-card" key={i}>
                                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>{item.label}</div>
                                    <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', marginBottom: '10px' }} />
                                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.3rem', color: item.color }}>{item.val}</div>
                                </div>
                            ))}
                        </div>

                        {/* VENTES DU MOIS */}
                        <div style={{ background: 'rgba(10,15,30,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '20px', padding: '20px', marginBottom: '16px' }}>
                            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1rem', color: '#F1F5F9', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                                Sales: <span style={{ color: '#34D399' }}>{new Date().toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</span>
                            </div>
                            <div className="stats-cards">
                                {[
                                    { label: 'CA',        val: `${revenue.current_month_total} $`,       color: '#34D399' },
                                    { label: 'Publicité', val: `${revenue.current_month_promotions} $`,  color: '#8B5CF6' },
                                    { label: 'Boosts',    val: `${revenue.current_month_boosts} $`,      color: '#F59E0B' },
                                ].map((item, i) => (
                                    <div className="stat-card" key={i}>
                                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>{item.label}</div>
                                        <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', marginBottom: '10px' }} />
                                        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.3rem', color: item.color }}>{item.val}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* HISTORIQUE VENTES */}
                        <div className="admin-section" style={{ marginBottom: '16px' }}>
                            <div className="admin-section-title">
                                Monthly sales history
                                <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{revenue.sales_this_month.length} sale{revenue.sales_this_month.length > 1 ? 's' : ''}</span>
                            </div>
                            {revenue.sales_this_month.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>No sales this month</div>
                            ) : (
                                <table className="admin-table">
                                    <thead><tr><th>Date</th><th>Project</th><th>Service</th><th>Duration / Pack</th><th>Amount</th></tr></thead>
                                    <tbody>
                                        {revenue.sales_this_month.map((sale, i) => (
                                            <tr key={i}>
                                                <td style={{ color: '#94a3b8' }}>{new Date(sale.created_at).toLocaleDateString('en-GB')}</td>
                                                <td style={{ fontWeight: '600' }}>{sale.project_name}</td>
                                                <td>
                                                    <span style={{ background: sale.sale_type === 'boost' ? 'rgba(245,158,11,0.12)' : 'rgba(139,92,246,0.12)', color: sale.sale_type === 'boost' ? '#F59E0B' : '#a78bfa', border: `1px solid ${sale.sale_type === 'boost' ? 'rgba(245,158,11,0.3)' : 'rgba(139,92,246,0.3)'}`, borderRadius: '20px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: '600' }}>
                                                        {sale.type}
                                                    </span>
                                                </td>
                                                <td style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{sale.duration}</td>
                                                <td style={{ color: '#34D399', fontWeight: '700' }}>{sale.amount} $</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* CLASSEMENT + CAMEMBERT */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div className="admin-section">
                                <div className="admin-section-title">Classement des services</div>
                                {revenue.services_ranking.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>Aucune donnée</div>
                                ) : (
                                    <div className="top-promo-list">
                                        {revenue.services_ranking.map((item, i) => (
                                            <div className="top-promo-item" key={i}>
                                                <div className="top-promo-rank">#{i + 1}</div>
                                                <div className="top-promo-type" style={{ fontSize: '0.85rem' }}>{item.service}</div>
                                                <div className="top-promo-count">{item.count}x</div>
                                                <div className="top-promo-amount">{parseFloat(item.total).toFixed(0)} $</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="admin-section">
                                <div className="admin-section-title">Répartition</div>
                                {revenue.services_ranking.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>Aucune donnée</div>
                                ) : (() => {
                                    const totalAll = revenue.services_ranking.reduce((acc, s) => acc + parseFloat(s.total), 0);
                                    const pieColors = ['#34D399', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#10B981', '#F97316'];
                                    let cumulative = 0;
                                    const size = 160, cx = size / 2, cy = size / 2, r = 60;
                                    const slices = revenue.services_ranking.map((item, i) => {
                                        const pct = parseFloat(item.total) / totalAll;
                                        const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
                                        cumulative += pct;
                                        const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
                                        const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
                                        const x2 = cx + r * Math.cos(endAngle),   y2 = cy + r * Math.sin(endAngle);
                                        return { path: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${x2} ${y2} Z`, color: pieColors[i % pieColors.length], pct: Math.round(pct * 100), label: item.service };
                                    });
                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
                                                {slices.map((s, i) => <path key={i} d={s.path} fill={s.color} opacity={0.85} />)}
                                            </svg>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                {slices.map((s, i) => (
                                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: s.color, flexShrink: 0 }} />
                                                        <span style={{ color: '#E2E8F0', fontSize: '0.78rem' }}>{s.label} <span style={{ color: s.color, fontWeight: '600' }}>{s.pct}%</span></span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* GRAPHIQUES */}
                        {revenue.monthly.length > 0 && (
                            <div className="chart-wrap">
                                <div className="chart-title">Revenus par mois (12 derniers mois)</div>
                                <div className="bar-chart">
                                    {(() => {
                                        const max = Math.max(...revenue.monthly.map(m => parseFloat(m.total)), 1);
                                        return revenue.monthly.map((m, i) => (
                                            <div className="bar-item" key={i}>
                                                <div className="bar-val">{parseFloat(m.total) > 0 ? parseFloat(m.total).toFixed(0) : ''}</div>
                                                <div className="bar" style={{ height: `${Math.max((parseFloat(m.total) / max) * 120, 4)}px`, background: 'linear-gradient(180deg, #34D399, #10B981)' }} />
                                                <div className="bar-label">{m.month.substring(5)}/{m.month.substring(2, 4)}</div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                        )}
                        {revenue.yearly.length > 0 && (
                            <div className="chart-wrap">
                                <div className="chart-title">Revenus par année</div>
                                <div className="bar-chart">
                                    {(() => {
                                        const max = Math.max(...revenue.yearly.map(y => parseFloat(y.total)), 1);
                                        return revenue.yearly.map((y, i) => (
                                            <div className="bar-item" key={i}>
                                                <div className="bar-val">{parseFloat(y.total).toFixed(0)} $</div>
                                                <div className="bar" style={{ height: `${Math.max((parseFloat(y.total) / max) * 120, 4)}px`, background: 'linear-gradient(180deg, #8B5CF6, #6D28D9)' }} />
                                                <div className="bar-label">{y.year}</div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ======================== PROJETS ======================== */}
                {tab === 'projets' && (
                    <div className="admin-section">
                        <div className="admin-section-title">
                            Tous les projets
                            <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{projects.length} projet{projects.length > 1 ? 's' : ''}</span>
                        </div>

                        {/* FILTRES */}
                        <div className="filter-tabs">
                            {[
                                ['all',      'Tous',       projectStats.total],
                                ['pending',  'En attente', projectStats.pending],
                                ['approved', 'En ligne',   projectStats.approved],
                                ['rejected', 'Refusés',    projectStats.rejected],
                            ].map(([key, label, count]) => (
                                <button key={key} className={`admin-tab${projectFilter === key ? ' active' : ''}`} onClick={() => loadProjects(key)}>
                                    {label} {count > 0 && <span style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '20px', padding: '1px 8px', fontSize: '0.72rem', marginLeft: '4px' }}>{count}</span>}
                                </button>
                            ))}
                        </div>

                        {/* RECHERCHE PROJETS */}
                        <div style={{ position: 'relative', marginBottom: '16px', maxWidth: '360px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '1rem' }}>⌕</span>
                            <input
                                value={projectSearch}
                                onChange={e => setProjectSearch(e.target.value)}
                                placeholder="Search by name, ticker or client..."
                                style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '9px 12px 9px 36px', color: '#fff', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
                            />
                            {projectSearch && (
                                <button onClick={() => setProjectSearch('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
                            )}
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ cursor: 'pointer', textAlign: 'center' }} onClick={() => handleSort('status')}>🌍</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Projet{sortIcon('name')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('client')}>Client{sortIcon('client')}</th>
                                    <th style={{ cursor: 'pointer', textAlign: 'center' }} onClick={() => handleSort('state')}>Etat{sortIcon('state')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('total_paid')}>USDT{sortIcon('total_paid')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('votes')}>Votes{sortIcon('votes')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('boosts')}>Boosts{sortIcon('boosts')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('created_at')}>Date{sortIcon('created_at')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('pubs')}>Pubs actives{sortIcon('pubs')}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedProjects.map(p => (
                                    <tr key={p.id}>

                                        {/* STATUT */}
                                        <td style={{ textAlign: 'center' }}>
                                            <div title={parseInt(p.is_approved) === 1 ? 'En ligne' : 'Hors ligne'} style={{ width: '10px', height: '10px', borderRadius: '50%', background: parseInt(p.is_approved) === 1 ? '#34D399' : '#f87171', boxShadow: `0 0 5px ${parseInt(p.is_approved) === 1 ? '#34D399' : '#f87171'}`, margin: '0 auto' }} />
                                        </td>

                                        {/* PROJET */}
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: colors[p.id % colors.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '800', color: '#0B1120', flexShrink: 0 }}>
                                                    {p.name.substring(0, 2).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: '600', cursor: 'pointer', color: '#F1F5F9' }} onClick={() => navigate(`/admin/projet/${p.id}`)}>
                                                        {p.name}
                                                    </div>
                                                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{p.ticker} · {p.chain}</div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* CLIENT */}
                                        <td style={{ color: '#34D399', fontSize: '0.82rem', fontWeight: '600' }}>
                                            {p.owner || <span style={{ color: '#475569' }}>—</span>}
                                        </td>

                                        {/* ETAT */}
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{
                                                fontSize: '0.72rem', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', display: 'inline-block',
                                                background: p.status === 'live' ? 'rgba(52,211,153,0.1)' : p.status === 'upcoming' ? 'rgba(245,158,11,0.1)' : 'rgba(100,116,139,0.15)',
                                                color: p.status === 'live' ? '#34D399' : p.status === 'upcoming' ? '#F59E0B' : '#94a3b8',
                                                border: `1px solid ${p.status === 'live' ? 'rgba(52,211,153,0.3)' : p.status === 'upcoming' ? 'rgba(245,158,11,0.3)' : 'rgba(100,116,139,0.3)'}`,
                                            }}>
                                                {p.status === 'live' ? 'Live' : p.status === 'upcoming' ? 'Upcoming' : 'Ended'}
                                            </span>
                                        </td>

                                        {/* USDT */}
                                        <td style={{ color: parseFloat(p.total_paid) > 0 ? '#34D399' : '#475569', fontWeight: '600', fontSize: '0.85rem' }}>
                                            {parseFloat(p.total_paid) > 0 ? parseFloat(p.total_paid).toFixed(0) + ' $' : '—'}
                                        </td>

                                        {/* VOTES */}
                                        <td style={{ color: '#34D399', fontWeight: '600', fontSize: '0.85rem' }}>{p.total_votes}</td>

                                        {/* BOOSTS */}
                                        <td style={{ color: '#F59E0B', fontWeight: '600', fontSize: '0.85rem' }}>{p.boosts || 0}</td>

                                        {/* DATE */}
                                        <td style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{new Date(p.created_at).toLocaleDateString('en-GB')}</td>

                                        {/* PUBS ACTIVES */}
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                {p.has_banner && (
                                                    <span style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '10px', padding: '4px 8px', fontSize: '0.72rem', fontWeight: '600', display: 'inline-flex', flexDirection: 'column', gap: '2px' }}>
                                                        <span>📢 Banner</span>
                                                        {p.banner_until && (
                                                            <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '0.68rem' }}>
                                                                until {new Date(p.banner_until).toLocaleDateString('en-GB')}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                                {p.has_premium && (
                                                    <span style={{ background: 'rgba(139,92,246,0.12)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '10px', padding: '4px 8px', fontSize: '0.72rem', fontWeight: '600', display: 'inline-flex', flexDirection: 'column', gap: '2px' }}>
                                                        <span>⭐ Premium</span>
                                                        {p.premium_until && (
                                                            <span style={{ color: '#94a3b8', fontWeight: '400', fontSize: '0.68rem' }}>
                                                                until {new Date(p.premium_until).toLocaleDateString('en-GB')}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                                {p.has_card_image && (
                                                    <span style={{ background: 'rgba(236,72,153,0.12)', color: '#EC4899', border: '1px solid rgba(236,72,153,0.3)', borderRadius: '20px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: '600' }}>🖼️ Card Image</span>
                                                )}
                                                {!p.has_banner && !p.has_premium && !p.has_card_image && (
                                                    <span style={{ color: '#475569', fontSize: '0.75rem' }}>—</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* ACTIONS */}
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                {parseInt(p.is_approved) === 0 && parseInt(p.email_verified) === 1 && (
                                                    <button title="Valider le projet" onClick={() => handleProjectAction('approve', p.id)}
                                                        style={{ background: 'none', border: 'none', color: '#34D399', cursor: 'pointer', fontSize: '1rem', padding: 0 }}
                                                        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                                                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>✓</button>
                                                )}
                                                {parseInt(p.is_approved) === 1 && (
                                                    <button title="Désactiver le projet" onClick={() => handleProjectAction('reject', p.id)}
                                                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem', padding: 0 }}
                                                        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                                                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>⊘</button>
                                                )}
                                                <button title="Supprimer le projet"
                                                    onClick={() => { if (window.confirm('Delete this project?')) handleProjectAction('delete', p.id); }}
                                                    style={{ background: 'none', border: '1px solid rgba(148,163,184,0.3)', borderRadius: '6px', color: '#94a3b8', cursor: 'pointer', fontSize: '0.75rem', padding: '3px 7px', transition: 'all 0.2s' }}
                                                    onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(248,113,113,0.4)'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = 'rgba(148,163,184,0.3)'; }}>✕</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ======================== BOOSTS ======================== */}
                {tab === 'boosts' && (
                    <div className="admin-section">
                        <div className="admin-section-title">
                            Boosts & Publicités
                            <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{boosts.length} entrée{boosts.length > 1 ? 's' : ''}</span>
                        </div>

                        {/* FILTRES BOOSTS */}
                        <div className="filter-tabs" style={{ marginBottom: '16px' }}>
                            {[['pending', 'En attente'], ['confirmed', 'Confirmés'], ['rejected', 'Rejetés'], ['all', 'Tous']].map(([key, label]) => (
                                <button key={key} className={`admin-tab${boostFilter === key ? ' active' : ''}`} onClick={() => loadBoosts(key)}>{label}</button>
                            ))}
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Projet</th>
                                    <th>Type</th>
                                    <th>Offre</th>
                                    <th>Quantité</th>
                                    <th>Montant</th>
                                    <th>Tx Hash</th>
                                    <th>User</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {boosts.map(b => (
                                    <tr key={b.id}>
                                        <td style={{ fontWeight: '600' }}>{b.project_name}</td>
                                        <td>
                                            <span style={{
                                                color: b.promo_type === 'boost' ? '#F59E0B' : b.promo_type === 'banner' ? '#3B82F6' : b.promo_type === 'card_image' ? '#EC4899' : '#8B5CF6',
                                                fontWeight: '600', fontSize: '0.82rem'
                                            }}>
                                                {b.promo_type === 'boost' ? '⚡' : b.promo_type === 'banner' ? '📢' : b.promo_type === 'card_image' ? '🖼️' : '⭐'} {b.promo_type}
                                            </span>
                                        </td>
                                        <td style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{b.setting_label || b.pack}</td>
                                        <td style={{ color: '#34D399', fontWeight: '600' }}>{parseInt(b.quantity) > 0 ? `+${b.quantity}` : '—'}</td>
                                        <td style={{ color: '#F1F5F9' }}>{b.amount} $</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#94a3b8' }}>{b.tx_hash.substring(0, 12)}...</span>
                                                <button onClick={() => { navigator.clipboard.writeText(b.tx_hash); setCopiedId(b.id); setTimeout(() => setCopiedId(null), 2000); }}
                                                    title="Copier le tx hash"
                                                    style={{ background: copiedId === b.id ? 'rgba(52,211,153,0.1)' : 'none', border: 'none', color: copiedId === b.id ? '#34D399' : '#94a3b8', cursor: 'pointer', fontSize: '0.8rem', padding: '2px 6px', borderRadius: '6px', transition: 'all 0.2s' }}>
                                                    {copiedId === b.id ? '✓' : '📋'}
                                                </button>
                                                <a href={`https://bscscan.com/tx/${b.tx_hash}`} target="_blank" rel="noopener noreferrer" title="Voir sur BSCScan" style={{ color: '#F59E0B', fontSize: '0.8rem', textDecoration: 'none' }}>🔍</a>
                                            </div>
                                        </td>
                                        <td style={{ color: '#94a3b8' }}>{b.username || 'Anonyme'}</td>
                                        <td style={{ color: '#94a3b8' }}>{new Date(b.created_at).toLocaleDateString('en-GB')}</td>
                                        <td>
                                            {b.status === 'pending' && (
                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                    <button className="action-btn btn-approve" onClick={() => handleBoostAction('confirm', b.id)}>✓ Confirmer</button>
                                                    <button className="action-btn btn-reject" onClick={() => handleBoostAction('reject', b.id)}>✕ Rejeter</button>
                                                </div>
                                            )}
                                            {b.status === 'confirmed' && <span style={{ color: '#34D399', fontSize: '0.8rem' }}>✓ Confirmé</span>}
                                            {b.status === 'rejected' && <span style={{ color: '#f87171', fontSize: '0.8rem' }}>✕ Rejeté</span>}
                                            {b.status === 'expired'  && <span style={{ color: '#64748b', fontSize: '0.8rem' }}>⏱ Expiré</span>}
                                        </td>
                                    </tr>
                                ))}
                                {boosts.length === 0 && (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>Aucune entrée</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ======================== UTILISATEURS ======================== */}
                {tab === 'utilisateurs' && (
                    <div className="admin-section">
                        <div className="admin-section-title">
                            Utilisateurs
                            <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}</span>
                        </div>

                        {/* RECHERCHE UTILISATEURS */}
                        <div style={{ position: 'relative', marginBottom: '16px', maxWidth: '360px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '1rem' }}>⌕</span>
                            <input
                                value={userSearch}
                                onChange={e => setUserSearch(e.target.value)}
                                placeholder="Search by username, email or project..."
                                style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '9px 12px 9px 36px', color: '#fff', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
                            />
                            {userSearch && (
                                <button onClick={() => setUserSearch('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
                            )}
                        </div>

                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleUserSort('username')}>Utilisateur{userSortIcon('username')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleUserSort('email')}>Email{userSortIcon('email')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleUserSort('role')}>Rôle{userSortIcon('role')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleUserSort('project_name')}>Projet{userSortIcon('project_name')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleUserSort('email_verified')}>Email vérifié{userSortIcon('email_verified')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleUserSort('is_active')}>Statut{userSortIcon('is_active')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => handleUserSort('created_at')}>Inscrit le{userSortIcon('created_at')}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(u => (
                                    <tr key={u.id} style={{ opacity: parseInt(u.is_active) === 0 ? 0.5 : 1 }}>
                                        <td style={{ fontWeight: '600' }}>{u.username}</td>
                                        <td style={{ color: '#94a3b8' }}>{u.email}</td>
                                        <td>
                                            <span style={{
                                                color: u.role === 'admin' ? '#F59E0B' : u.role === 'client' ? '#34D399' : '#a78bfa',
                                                fontWeight: '600', fontSize: '0.82rem'
                                            }}>
                                                {u.role === 'admin' ? '⚙ Admin' : u.role === 'client' ? '🚀 Client' : '🎯 Participant'}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '0.82rem' }}>
                                            {u.project_name
                                                ? <span style={{ color: '#F1F5F9', cursor: 'pointer' }} onClick={() => navigate(`/admin/projet/${u.id}`)}>{u.project_name}</span>
                                                : <span style={{ color: '#475569' }}>—</span>
                                            }
                                        </td>
                                        <td>{parseInt(u.email_verified) ? <span style={{ color: '#34D399' }}>✓</span> : <span style={{ color: '#f87171' }}>✕</span>}</td>
                                        <td>
                                            {parseInt(u.is_active) === 0
                                                ? <span style={{ color: '#f87171', fontSize: '0.78rem', fontWeight: '600' }}>⊘ Désactivé</span>
                                                : <span style={{ color: '#34D399', fontSize: '0.78rem' }}>● Actif</span>
                                            }
                                        </td>
                                        <td style={{ color: '#94a3b8' }}>{new Date(u.created_at).toLocaleDateString('en-GB')}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                {u.role !== 'admin' && (
                                                    parseInt(u.is_active) === 0
                                                        ? <button
                                                            title="Réactiver le compte"
                                                            onClick={() => handleUserAction('enable', u.id)}
                                                            style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '6px', color: '#34D399', cursor: 'pointer', fontSize: '0.75rem', padding: '3px 8px' }}>
                                                            ✓ Activer
                                                          </button>
                                                        : <button
                                                            title="Désactiver le compte"
                                                            onClick={() => { if (window.confirm(`Désactiver le compte de ${u.username} ?`)) handleUserAction('disable', u.id); }}
                                                            style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', color: '#F59E0B', cursor: 'pointer', fontSize: '0.75rem', padding: '3px 8px' }}>
                                                            ⊘ Désactiver
                                                          </button>
                                                )}
                                                {u.role !== 'admin' && (
                                                    <button
                                                        title="Supprimer le compte"
                                                        onClick={() => { if (window.confirm(`Supprimer définitivement le compte de ${u.username} ?`)) handleUserAction('delete', u.id); }}
                                                        style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '6px', color: '#f87171', cursor: 'pointer', fontSize: '0.75rem', padding: '3px 8px' }}>
                                                        🗑 Supprimer
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ======================== CARD IMAGES ======================== */}
                {tab === 'card images' && (
                    <div className="admin-section">
                        <div className="admin-section-title">
                            Bannières Card — Validation
                            <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{cardBanners.length} image{cardBanners.length > 1 ? 's' : ''}</span>
                        </div>

                        {/* FILTRES */}
                        <div className="filter-tabs" style={{ marginBottom: '16px' }}>
                            {[['pending', 'En attente'], ['approved', 'Approuvées'], ['rejected', 'Rejetées']].map(([key, label]) => (
                                <button key={key} className={`admin-tab${cardBannerFilter === key ? ' active' : ''}`} onClick={() => loadCardBanners(key)}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        {cardBanners.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem' }}>
                                No image {cardBannerFilter === 'pending' ? 'pending' : cardBannerFilter === 'approved' ? 'approved' : 'rejected'}
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                                {cardBanners.map(item => (
                                    <div key={item.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' }}>
                                        {/* IMAGE PREVIEW */}
                                        <div style={{ position: 'relative', width: '100%', height: '140px', background: '#0B1120', overflow: 'hidden' }}>
                                            <img
                                                src={item.card_banner_url}
                                                alt={item.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                onError={e => { e.currentTarget.style.display = 'none'; }}
                                            />
                                            {/* STATUS BADGE */}
                                            <div style={{
                                                position: 'absolute', top: '8px', right: '8px',
                                                background: cardBannerFilter === 'pending' ? 'rgba(245,158,11,0.9)' : cardBannerFilter === 'approved' ? 'rgba(52,211,153,0.9)' : 'rgba(248,113,113,0.9)',
                                                color: '#fff', borderRadius: '20px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: '700'
                                            }}>
                                                {cardBannerFilter === 'pending' ? '⏳ En attente' : cardBannerFilter === 'approved' ? '✓ Approuvée' : '✕ Rejetée'}
                                            </div>
                                        </div>

                                        {/* INFOS PROJET */}
                                        <div style={{ padding: '12px 14px' }}>
                                            <div style={{ fontWeight: '700', color: '#F1F5F9', fontSize: '0.95rem', marginBottom: '4px' }}>{item.name}</div>
                                            <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '12px' }}>
                                                👤 {item.username} · {item.email}
                                            </div>

                                            {/* URL directe (debug) */}
                                            <div style={{ fontSize: '0.7rem', color: '#475569', wordBreak: 'break-all', marginBottom: '12px', fontFamily: 'monospace' }}>
                                                {item.card_banner_url}
                                            </div>

                                            {/* ACTIONS */}
                                            {cardBannerFilter === 'pending' && (
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button
                                                        onClick={() => handleCardBannerAction('approve', item.id)}
                                                        className="action-btn btn-approve"
                                                        style={{ flex: 1 }}>
                                                        ✓ Approuver
                                                    </button>
                                                    <button
                                                        onClick={() => { if (window.confirm(`Rejeter l'image de "${item.name}" ? Le client devra en soumettre une nouvelle.`)) handleCardBannerAction('reject', item.id); }}
                                                        className="action-btn btn-reject"
                                                        style={{ flex: 1 }}>
                                                        ✕ Rejeter
                                                    </button>
                                                </div>
                                            )}
                                            {cardBannerFilter === 'approved' && (
                                                <button
                                                    onClick={() => { if (window.confirm(`Retirer l'approbation de l'image de "${item.name}" ?`)) handleCardBannerAction('reject', item.id); }}
                                                    className="action-btn btn-reject"
                                                    style={{ width: '100%' }}>
                                                    ✕ Révoquer
                                                </button>
                                            )}
                                            {cardBannerFilter === 'rejected' && (
                                                <div style={{ color: '#f87171', fontSize: '0.8rem' }}>Image retirée — le client peut soumettre une nouvelle image.</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ======================== PARAMÈTRES ======================== */}
                {tab === 'paramètres' && (
                    <div className="admin-section">
                        <div className="admin-section-title">Paramètres — Tarifs</div>
                        {settingsSaved && (
                            <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)', borderRadius: '12px', padding: '10px 14px', color: '#34D399', fontSize: '0.85rem', marginBottom: '16px' }}>
                                ✓ Paramètres sauvegardés
                            </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                            {[
                                { prefix: 'boost',      color: '#F59E0B', icon: '⚡' },
                                { prefix: 'banner',     color: '#3B82F6', icon: '📢' },
                                { prefix: 'premium',    color: '#8B5CF6', icon: '⭐' },
                                { prefix: 'card_image', color: '#EC4899', icon: '🖼️' },
                            ].map(({ prefix, color, icon }) => (
                                <div key={prefix}>
                                    <div style={{ fontSize: '0.75rem', color, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${color}33` }}>{icon} {prefix}</div>
                                    {settings.filter(s => s.key.startsWith(prefix)).map((s, i) => (
                                        <div key={i} style={{ marginBottom: '12px' }}>
                                            <label style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px', display: 'block' }}>{s.label}</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input className="form-input" type="number" value={s.value}
                                                    onChange={e => { const u = [...settings]; u[settings.indexOf(s)] = { ...s, value: e.target.value }; setSettings(u); }}
                                                    style={{ flex: 1 }} />
                                                <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>$</span>
                                                <button onClick={async () => { await updateSetting(s.key, s.value); setSettingsSaved(true); setTimeout(() => setSettingsSaved(false), 3000); }}
                                                    style={{ background: 'none', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '6px', color: '#34D399', cursor: 'pointer', fontSize: '0.75rem', padding: '5px 10px' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(52,211,153,0.1)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>✓</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>

                        {/* ── Section Bulle ── */}
                        <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                            <div style={{ fontSize: '0.75rem', color: '#34D399', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>🫧 Calendrier bulle</div>
                            {(() => {
                                const s = settings.find(s => s.key === 'calendar_show_live');
                                if (!s) return <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Paramètre introuvable — exécute le SQL d'installation.</div>;
                                const isOn = s.value === '1';
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '14px 18px' }}>
                                        <div>
                                            <div style={{ color: '#F1F5F9', fontWeight: '600', fontSize: '0.88rem', marginBottom: '3px' }}>Afficher les projets Live</div>
                                            <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Les projets avec statut "Live" apparaissent en premier dans le calendrier bulle.</div>
                                        </div>
                                        <div
                                            onClick={async () => {
                                                const newVal = isOn ? '0' : '1';
                                                await updateSetting('calendar_show_live', newVal);
                                                const u = settings.map(x => x.key === 'calendar_show_live' ? { ...x, value: newVal } : x);
                                                setSettings(u);
                                                setSettingsSaved(true);
                                                setTimeout(() => setSettingsSaved(false), 3000);
                                            }}
                                            style={{ width: '44px', height: '24px', borderRadius: '12px', background: isOn ? '#34D399' : 'rgba(255,255,255,0.1)', border: isOn ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', position: 'relative', transition: 'all 0.2s', flexShrink: 0 }}>
                                            <div style={{ position: 'absolute', top: '3px', left: isOn ? '22px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </div>

            {/* ======================== RELANCE ======================== */}
            {tab === 'relance' && (
                <div>
                    {/* TEMPLATES */}
                    <div style={{ marginBottom: '28px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <div style={{ color: '#f1f5f9', fontWeight: '700', fontSize: '1rem' }}>📝 Messages pré-enregistrés</div>
                            <button className="action-btn btn-approve" style={{ padding: '7px 16px', fontSize: '0.82rem' }}
                                onClick={() => { setTmplForm({ name: '', type: 'manual', subject: '', body: '' }); setTmplModal('new'); }}>
                                + Nouveau template
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {relanceTemplates.map(t => (
                                <div key={t.id} onClick={() => setRelanceTemplateId(t.id)}
                                    style={{
                                        padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', minWidth: '220px',
                                        background: relanceTemplateId === t.id ? 'rgba(52,211,153,0.12)' : 'rgba(255,255,255,0.04)',
                                        border: `1px solid ${relanceTemplateId === t.id ? 'rgba(52,211,153,0.5)' : 'rgba(255,255,255,0.08)'}`,
                                    }}>
                                    <div style={{ color: relanceTemplateId === t.id ? '#34D399' : '#f1f5f9', fontWeight: '600', fontSize: '0.88rem', marginBottom: '4px' }}>{t.name}</div>
                                    <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '6px' }}>{t.subject}</div>
                                    <div style={{ marginBottom: '10px' }}>
                                        {t.type === 'relance_boost' && <span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: '4px', background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>⚡ Cron boost</span>}
                                        {t.type === 'relance_quests' && <span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: '4px', background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.3)' }}>🎯 Cron quests</span>}
                                        {t.type === 'relance_kyc_audit' && <span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: '4px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)' }}>🔒 Cron KYC (one-shot)</span>}
                                        {t.type === 'relance_no_account' && <span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: '4px', background: 'rgba(249,115,22,0.15)', color: '#fb923c', border: '1px solid rgba(249,115,22,0.3)' }}>👤 Cron sans compte</span>}
                                        {(!t.type || t.type === 'manual') && <span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: '4px', background: 'rgba(100,116,139,0.15)', color: '#94a3b8', border: '1px solid rgba(100,116,139,0.3)' }}>✉️ Manuel</span>}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button className="action-btn" style={{ padding: '4px 10px', fontSize: '0.72rem', background: 'rgba(99,179,237,0.15)', color: '#63b3ed', border: '1px solid rgba(99,179,237,0.3)' }}
                                            onClick={e => { e.stopPropagation(); openEditTemplate(t); }}>Modifier</button>
                                        <button className="action-btn btn-reject" style={{ padding: '4px 10px', fontSize: '0.72rem' }}
                                            onClick={e => { e.stopPropagation(); handleDeleteTemplate(t.id); }}>Supprimer</button>
                                    </div>
                                </div>
                            ))}
                            {relanceTemplates.length === 0 && (
                                <div style={{ color: '#475569', fontSize: '0.85rem' }}>Aucun template. Créez-en un ci-dessus.</div>
                            )}
                        </div>
                    </div>

                    {/* FILTRES + RECHERCHE */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text" placeholder="Rechercher un projet..." value={relanceSearch}
                            onChange={e => setRelanceSearch(e.target.value)}
                            style={{ padding: '8px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#f1f5f9', fontSize: '0.85rem', width: '240px' }}
                        />
                        {[
                            { key: 'all',        label: 'Tous',            tooltip: 'Affiche tous les projets, qu\'ils soient relançables ou non.' },
                            { key: 'eligible',   label: '✅ Relançables',  tooltip: 'Projets à qui tu peux envoyer un email maintenant.\nJamais relancés, ou cooldown de 14 jours écoulé.\n→ Sélectionne-les et envoie ta relance !' },
                            { key: 'ineligible', label: '⏳ En cooldown',  tooltip: 'Projets ayant déjà reçu une relance il y a moins de 14 jours.\nIls redeviendront éligibles automatiquement.\n→ Survole leur badge orange pour voir la date exacte.' },
                        ].map(f => (
                            <div key={f.key} style={{ position: 'relative' }}
                                onMouseEnter={e => { const t = e.currentTarget.querySelector('.relance-tooltip'); if(t) t.style.opacity='1'; t.style.pointerEvents='auto'; }}
                                onMouseLeave={e => { const t = e.currentTarget.querySelector('.relance-tooltip'); if(t) t.style.opacity='0'; t.style.pointerEvents='none'; }}>
                                <button onClick={() => setRelanceFilter(f.key)}
                                    style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '0.8rem', cursor: 'pointer',
                                        background: relanceFilter === f.key ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.05)',
                                        border: `1px solid ${relanceFilter === f.key ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.08)'}`,
                                        color: relanceFilter === f.key ? '#34D399' : '#94a3b8' }}>
                                    {f.label}
                                </button>
                                <div className="relance-tooltip" style={{
                                    opacity: 0, pointerEvents: 'none', transition: 'opacity 0.18s',
                                    position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)',
                                    background: '#0f1e38', border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: '10px', padding: '10px 14px', zIndex: 999,
                                    width: '230px', whiteSpace: 'pre-line',
                                    color: '#94a3b8', fontSize: '0.75rem', lineHeight: '1.6',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                                }}>
                                    {f.tooltip}
                                </div>
                            </div>
                        ))}
                        <div style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.8rem' }}>
                            {relanceSelected.length} sélectionné{relanceSelected.length > 1 ? 's' : ''}
                        </div>
                    </div>

                    {/* TABLEAU */}
                    <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748b' }}>
                                        <input type="checkbox"
                                            checked={allEligibleIds.length > 0 && relanceSelected.length === allEligibleIds.length}
                                            onChange={toggleSelectAll}
                                            title="Sélectionner tous les éligibles"
                                        />
                                    </th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748b', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleRelanceSort('name')}>Projet{relanceSortIcon('name')}</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748b', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleRelanceSort('type')}>Type{relanceSortIcon('type')}</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748b', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleRelanceSort('owner_email')}>Email owner{relanceSortIcon('owner_email')}</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'center', color: '#64748b', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleRelanceSort('active_boosts')}>Boost{relanceSortIcon('active_boosts')}</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'center', color: '#64748b', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleRelanceSort('quest_count')}>Quests{relanceSortIcon('quest_count')}</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748b', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleRelanceSort('last_relance_sent')}>Dernier email envoyé{relanceSortIcon('last_relance_sent')}</th>
                                    <th style={{ padding: '10px 8px', textAlign: 'center', color: '#64748b', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleRelanceSort('relance_eligible')}>Statut{relanceSortIcon('relance_eligible')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {relanceFiltered.map(p => {
                                    const eligible = parseInt(p.relance_eligible) === 1;
                                    return (
                                        <tr key={p.id} style={{
                                            borderBottom: '1px solid rgba(255,255,255,0.04)',
                                            opacity: eligible ? 1 : 0.45,
                                            background: relanceSelected.includes(p.id) ? 'rgba(52,211,153,0.06)' : 'transparent',
                                        }}>
                                            <td style={{ padding: '10px 8px' }}>
                                                <input type="checkbox"
                                                    checked={relanceSelected.includes(p.id)}
                                                    disabled={!eligible}
                                                    onChange={() => toggleRelanceSelect(p.id)}
                                                />
                                            </td>
                                            <td style={{ padding: '10px 8px', color: '#f1f5f9', fontWeight: '600' }}>{p.name}</td>
                                            <td style={{ padding: '10px 8px', fontSize: '0.75rem' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <span style={{ color: '#94a3b8', textTransform: 'uppercase' }}>{p.type}</span>
                                                    {(parseInt(p.kyc) === 0 || !p.kyc) && (parseInt(p.audit) === 0 || !p.audit) && (
                                                        <span title={p.kyc_relance_sent ? `Relance envoyée le ${new Date(p.kyc_relance_sent).toLocaleDateString('fr-FR')}` : 'Relance KYC & Audit non encore envoyée'}
                                                            style={{ background: p.kyc_relance_sent ? 'rgba(100,116,139,0.15)' : 'rgba(139,92,246,0.12)', color: p.kyc_relance_sent ? '#64748b' : '#a78bfa', border: `1px solid ${p.kyc_relance_sent ? 'rgba(100,116,139,0.3)' : 'rgba(139,92,246,0.3)'}`, borderRadius: '20px', padding: '2px 8px', fontSize: '0.7rem', fontWeight: '600' }}>
                                                            {p.kyc_relance_sent ? '📧 KYC envoyé' : '🔐 KYC & Audit'}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ padding: '10px 8px', color: '#94a3b8' }}>{p.owner_email || '—'}</td>
                                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                {parseInt(p.active_boosts) > 0 ? '✅' : '❌'}
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                {parseInt(p.quest_count) > 0 ? '✅' : '❌'}
                                            </td>
                                            <td style={{ padding: '10px 8px', color: '#94a3b8', fontSize: '0.78rem' }}>
                                                {p.last_relance_sent
                                                    ? new Date(p.last_relance_sent).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                    : <span style={{ color: '#475569' }}>Jamais</span>}
                                            </td>
                                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                                {eligible
                                                    ? <span style={{ background: 'rgba(52,211,153,0.15)', color: '#34D399', borderRadius: '20px', padding: '2px 10px', fontSize: '0.72rem' }}>Éligible</span>
                                                    : <span style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', borderRadius: '20px', padding: '2px 10px', fontSize: '0.72rem' }} title={`Disponible le ${new Date(p.relance_available_at).toLocaleDateString('fr-FR')}`}>
                                                        Cooldown
                                                    </span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {relanceFiltered.length === 0 && (
                                    <tr><td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#475569' }}>Aucun projet trouvé</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* BOUTON ENVOI */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <button className="action-btn btn-approve"
                            disabled={relanceSelected.length === 0 || !relanceTemplateId}
                            style={{ padding: '10px 24px', fontSize: '0.9rem', opacity: (relanceSelected.length === 0 || !relanceTemplateId) ? 0.4 : 1 }}
                            onClick={() => setRelanceConfirm(true)}>
                            📧 Envoyer la relance ({relanceSelected.length})
                        </button>
                        {relanceResult && (
                            <div style={{ color: '#34D399', fontSize: '0.85rem' }}>
                                ✅ {relanceResult.sent} envoyé{relanceResult.sent > 1 ? 's' : ''}
                                {relanceResult.skipped > 0 && ` · ${relanceResult.skipped} ignoré(s) (cooldown)`}
                                {relanceResult.errors?.length > 0 && <span style={{ color: '#f87171' }}> · {relanceResult.errors.length} erreur(s)</span>}
                            </div>
                        )}
                    </div>

                    {/* MODAL CONFIRMATION ENVOI */}
                    {relanceConfirm && (
                        <div className="reject-modal" onClick={() => setRelanceConfirm(false)}>
                            <div className="reject-modal-inner" onClick={e => e.stopPropagation()}>
                                <div className="reject-modal-title">Confirmer l'envoi</div>
                                <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: '12px 0' }}>
                                    Vous allez envoyer <strong style={{ color: '#f1f5f9' }}>{relanceSelected.length} email{relanceSelected.length > 1 ? 's' : ''}</strong> avec le template <strong style={{ color: '#34D399' }}>"{relanceTemplates.find(t => t.id === relanceTemplateId)?.name}"</strong>.<br />
                                    Les projets en cooldown seront automatiquement ignorés.
                                </p>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                                    <button className="btn-prev" onClick={() => setRelanceConfirm(false)}>Annuler</button>
                                    <button className="action-btn btn-approve" style={{ padding: '10px 20px', fontSize: '0.9rem' }}
                                        disabled={relanceSending}
                                        onClick={handleSendRelance}>
                                        {relanceSending ? 'Envoi...' : '✅ Confirmer l\'envoi'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MODAL TEMPLATE */}
                    {tmplModal && (
                        <div className="reject-modal" onClick={() => setTmplModal(null)}>
                            <div className="reject-modal-inner" style={{ maxWidth: '600px', width: '95%' }} onClick={e => e.stopPropagation()}>
                                <div className="reject-modal-title">{tmplModal === 'new' ? 'Nouveau template' : 'Modifier le template'}</div>
                                <div className="form-group" style={{ marginTop: '16px' }}>
                                    <label className="form-label">Nom du template</label>
                                    <input className="form-input" value={tmplForm.name} onChange={e => setTmplForm(f => ({ ...f, name: e.target.value }))} placeholder="ex: Boost your project" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Type / Action cron</label>
                                    <select className="form-input" value={tmplForm.type} onChange={e => setTmplForm(f => ({ ...f, type: e.target.value }))}>
                                        <option value="manual">Manuel uniquement (pas de cron)</option>
                                        <option value="relance_boost">Cron — Relance boost (projet sans boost actif)</option>
                                        <option value="relance_quests">Cron — Relance quests (projet sans quests)</option>
                                        <option value="relance_kyc_audit">Cron — Relance KYC & Audit (one-shot)</option>
                                        <option value="relance_no_account">Cron — Relance sans compte (inciter à créer un compte)</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Sujet de l'email</label>
                                    <input className="form-input" value={tmplForm.subject} onChange={e => setTmplForm(f => ({ ...f, subject: e.target.value }))} placeholder="ex: Give your project more visibility 🚀" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Corps de l'email (HTML) — placeholders : {'{{project_name}}'}, {'{{project_slug}}'}</label>
                                    <textarea className="form-textarea" rows={8} value={tmplForm.body} onChange={e => setTmplForm(f => ({ ...f, body: e.target.value }))} placeholder="<p>Hi {{project_name}} team,</p>..." />
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                                    <button className="btn-prev" onClick={() => setTmplModal(null)}>Annuler</button>
                                    <button className="action-btn btn-approve" style={{ padding: '10px 20px', fontSize: '0.9rem' }}
                                        onClick={handleSaveTemplate}>
                                        Sauvegarder
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* MODAL REJET */}
            {rejectModal && (
                <div className="reject-modal" onClick={() => setRejectModal(null)}>
                    <div className="reject-modal-inner" onClick={e => e.stopPropagation()}>
                        <div className="reject-modal-title">Rejeter — {rejectModal.name}</div>
                        <div className="form-group">
                            <label className="form-label">Raison du rejet</label>
                            <textarea className="form-textarea" placeholder="Expliquez pourquoi le projet est rejeté..." value={rejectNote} onChange={e => setRejectNote(e.target.value)} rows={3} />
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                            <button className="btn-prev" onClick={() => setRejectModal(null)}>Annuler</button>
                            <button className="action-btn btn-reject" style={{ padding: '10px 20px', fontSize: '0.9rem' }}
                                onClick={() => { handleProjectAction('reject', rejectModal.id, rejectNote); setRejectModal(null); setRejectNote(''); }}>
                                Confirmer le rejet
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}